import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ServiceSections";

/** Curated in-context internal links between service pages (SEO + navigation). */
const RELATED = {
  "/services/vapt": {
    intro:
      "Testing tells you what is exposed today. Detection tells you when someone tries it tomorrow — most clients pair VAPT with monitoring.",
    links: [
      ["/services/managed-security", "Managed Security Services", "24/7 monitoring and response for the gaps testing surfaces"],
      ["/services/cloud-security", "Cloud Security", "Posture hardening across Azure, AWS and GCP"],
      ["/insights", "Engineering insights", "DPDP and CERT-In readiness written by our engineers"],
    ],
  },
  "/services/managed-security": {
    intro:
      "Monitoring is only as good as the telemetry and the platform underneath it. These are the services teams usually combine with a managed SOC.",
    links: [
      ["/services/enterprise-siem", "Enterprise SIEM", "The detection platform and retention behind the SOC"],
      ["/services/open-source-soc", "Open Source SOC Model", "For SMBs across India that need CERT-In and DPDP coverage affordably"],
      ["/services/vapt", "VAPT", "Find what is exposed before the SOC has to catch it"],
    ],
  },
  "/services/solutions": {
    intro: "Each solution stands alone, and they compound when delivered together.",
    links: [
      ["/services/enterprise-landing-zone", "Enterprise Landing Zone", "The compliant cloud foundation everything else sits on"],
      ["/services/enterprise-siem", "Enterprise SIEM", "Detection engineering and compliance retention"],
      ["/services/managed-security", "Managed Security Services", "Have our engineers run it for you"],
    ],
  },
  "/services/enterprise-landing-zone": {
    intro: "A landing zone gives you guardrails. These services give you visibility inside them.",
    links: [
      ["/services/cloud-security", "Cloud Security", "Assess and harden the workloads running in the landing zone"],
      ["/services/enterprise-siem", "Enterprise SIEM", "Onboard the cloud control-plane logs you just enabled"],
      ["/services/vapt", "VAPT", "Validate the build with an independent test"],
    ],
  },
  "/services/enterprise-siem": {
    intro: "A SIEM is a platform, not an outcome. These are the two ways clients turn it into one.",
    links: [
      ["/services/managed-security", "Managed Security Services", "Analysts to work the queue the SIEM produces"],
      ["/services/enterprise-xdr", "Enterprise XDR", "Deeper endpoint, identity and email telemetry feeding it"],
      ["/services/open-source-soc", "Open Source SOC Model", "The same outcome without per-seat licence fees"],
    ],
  },
  "/services/enterprise-xdr": {
    intro: "XDR covers its own telemetry well. Pair it to cover everything else.",
    links: [
      ["/services/enterprise-siem", "Enterprise SIEM", "Retention and coverage for sources XDR cannot see"],
      ["/services/managed-security", "Managed Security Services", "24/7 humans behind the automated response"],
      ["/services/cloud-security", "Cloud Security", "Harden the cloud workloads XDR protects"],
    ],
  },
  "/services/cloud-security": {
    intro: "Hardening is a point in time. Keeping it that way needs structure and monitoring.",
    links: [
      ["/services/enterprise-landing-zone", "Enterprise Landing Zone", "Enforce the fixes as policy-as-code"],
      ["/services/managed-security", "Managed Security Services", "Continuous monitoring of cloud control planes"],
      ["/services/vapt", "VAPT", "Test the cloud-facing applications themselves"],
    ],
  },
  "/services/open-source-soc": {
    intro:
      "Built for SMBs across India. If you would rather we ran it, or you need testing evidence alongside it, start here.",
    links: [
      ["/services/managed-security", "Managed Security Services", "Fully managed 24/7 monitoring instead of self-run"],
      ["/services/vapt", "VAPT", "The testing evidence auditors ask for alongside monitoring"],
      ["/insights/cert-in-6-hour-reporting-180-day-log-retention", "CERT-In 6-hour and 180-day guide", "What the directions actually require of your architecture"],
    ],
  },
};

export const RelatedServices = ({ pathname }) => {
  const data = RELATED[pathname];
  if (!data) return null;

  return (
    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20 lg:px-16" data-testid="related-services">
      <Reveal>
        <Eyebrow>Where to next</Eyebrow>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">{data.intro}</p>
      </Reveal>
      <div className="mt-8 grid gap-4 sm:mt-10 md:grid-cols-3">
        {data.links.map(([to, title, copy], i) => (
          <Reveal key={to} delay={i * 0.08}>
            <Link
              to={to}
              className="glass-card group flex h-full flex-col p-5 sm:p-6"
              data-testid={`related-link-${to.replace(/\//g, "-").replace(/^-/, "")}`}
            >
              <h3 className="flex items-start justify-between gap-3 font-display text-base font-bold text-white">
                {title}
                <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </h3>
              <p className="mt-2.5 text-sm leading-relaxed text-[#DDE3F2]/90">{copy}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
